"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Z_INDEX_CLASS_NAMES } from "@/configs/z-index";
import { useVisualViewportHeight } from "@/hooks/use-visual-viewport-height";
import { cn } from "@/utils/class-name";

export const DialogCreateHandle: typeof DialogPrimitive.createHandle = DialogPrimitive.createHandle;

export const Dialog: typeof DialogPrimitive.Root = DialogPrimitive.Root;

export const DialogPortal: typeof DialogPrimitive.Portal = DialogPrimitive.Portal;

export function DialogTrigger(props: DialogPrimitive.Trigger.Props): React.ReactElement {
    return <DialogPrimitive.Trigger data-slot={"dialog-trigger"} {...props} />;
}

export function DialogClose(props: DialogPrimitive.Close.Props): React.ReactElement {
    return <DialogPrimitive.Close data-slot={"dialog-close"} {...props} />;
}

/** `::after` dim when `data-nested-dialog-open` is set. Blocks clicks so tapping the dimmed popup dismisses the dialog above. */
export const POPUP_COVERED_DIM =
    "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-black/32 after:opacity-0 after:transition-opacity after:duration-200 data-nested-dialog-open:after:pointer-events-auto data-nested-dialog-open:after:opacity-100";

export function DialogBackdrop({ className, ...props }: DialogPrimitive.Backdrop.Props): React.ReactElement {
    return (
        <DialogPrimitive.Backdrop
            className={cn(
                Z_INDEX_CLASS_NAMES.DIALOG,
                // Opacity only: animating `all` makes the compositor interpolate the blur every frame.
                "fixed inset-0 bg-black/32 backdrop-blur-sm transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0",
                className,
            )}
            data-slot={"dialog-backdrop"}
            {...props}
        />
    );
}

export function DialogViewport({ className, ...props }: DialogPrimitive.Viewport.Props): React.ReactElement {
    useVisualViewportHeight();

    return (
        <DialogPrimitive.Viewport
            className={cn(
                Z_INDEX_CLASS_NAMES.DIALOG,
                // Shrink with the visible viewport so an open keyboard does not cover fields.
                "fixed inset-x-0 bottom-0 top-[var(--nav-bar-height,55px)] grid max-h-[calc(var(--visual-viewport-height,100dvh)-var(--nav-bar-height,55px))] grid-rows-[1fr_auto_3fr] justify-items-center p-4 [--dialog-max-height:calc(var(--visual-viewport-height,100dvh)-var(--nav-bar-height,55px)-2rem)]",
                className,
            )}
            data-slot={"dialog-viewport"}
            {...props}
        />
    );
}

// Width only; `fullscreen` ignores this.
const dialogPopupVariants = cva("", {
    variants: {
        size: {
            default: "max-w-lg",
            md: "max-w-[768px] max-h-[min(80vh,var(--dialog-max-height,100%))]",
            lg: "max-w-6xl",
        },
    },
    defaultVariants: {
        size: "default",
    },
});

/** `centered` — desktop-centred, mobile top-anchored. `bottom-sheet` — mobile sheet. `fullscreen` — edge-to-edge above the nav bar. */
export type DialogPresentation = "centered" | "bottom-sheet" | "fullscreen";

export function DialogPopup({
    className,
    children,
    showCloseButton = true,
    presentation = "centered",
    fillHeight = false,
    size,
    closeProps,
    ...props
}: DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean;
    presentation?: DialogPresentation;
    size?: VariantProps<typeof dialogPopupVariants>["size"];
    fillHeight?: boolean;
    closeProps?: DialogPrimitive.Close.Props;
}): React.ReactElement {
    const isFullscreen = presentation === "fullscreen";
    const stickBottom = presentation === "bottom-sheet";
    const fillsViewportHeight = fillHeight || isFullscreen;
    const topAnchorOnMobile = !stickBottom && !fillsViewportHeight;

    return (
        <DialogPortal>
            <DialogBackdrop />
            <DialogViewport
                className={cn(
                    stickBottom &&
                        "max-sm:grid-rows-[1fr_auto] max-sm:p-0 max-sm:[--dialog-max-height:calc(var(--visual-viewport-height,100dvh)-var(--nav-bar-height,55px))]",
                    // Empty top row so the popup sits at the top on mobile.
                    topAnchorOnMobile && "max-sm:grid-rows-[0_auto_1fr]",
                    fillsViewportHeight && "grid-rows-[1fr]",
                    isFullscreen &&
                        "top-0 max-h-[var(--visual-viewport-height,100dvh)] p-5 [--dialog-max-height:calc(var(--visual-viewport-height,100dvh)-2.5rem)] max-sm:p-0 max-sm:[--dialog-max-height:var(--visual-viewport-height,100dvh)]",
                )}
            >
                <DialogPrimitive.Popup
                    className={cn(
                        "relative row-start-2 flex max-h-[var(--dialog-max-height,100%)] min-h-0 w-full min-w-0 origin-center flex-col rounded-2xl border bg-popover not-dark:bg-clip-padding text-popover-foreground shadow-lg/5 transition-[scale,opacity,translate] duration-200 ease-in-out will-change-transform before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-2xl)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 sm:data-ending-style:scale-98 sm:data-starting-style:scale-98 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
                        POPUP_COVERED_DIM,
                        stickBottom &&
                            "max-sm:max-w-none max-sm:origin-bottom max-sm:rounded-none max-sm:border-x-0 max-sm:border-t max-sm:border-b-0 max-sm:data-ending-style:translate-y-4 max-sm:data-starting-style:translate-y-4 max-sm:before:hidden max-sm:before:rounded-none",
                        topAnchorOnMobile &&
                            "max-sm:origin-top max-sm:data-ending-style:-translate-y-4 max-sm:data-starting-style:-translate-y-4",
                        fillsViewportHeight && "row-start-1 h-full",
                        isFullscreen
                            ? "max-w-none max-sm:rounded-none max-sm:border-0 max-sm:before:hidden"
                            : dialogPopupVariants({ size }),
                        className,
                    )}
                    data-slot={"dialog-popup"}
                    {...props}
                >
                    {children}
                    {showCloseButton && (
                        <DialogPrimitive.Close
                            aria-label={"Close"}
                            className={"absolute end-2 top-2"}
                            render={<Button size={"icon-lg"} variant={"ghost"} />}
                            {...closeProps}
                        >
                            <XIcon className={"size-6"} />
                        </DialogPrimitive.Close>
                    )}
                </DialogPrimitive.Popup>
            </DialogViewport>
        </DialogPortal>
    );
}

export function DialogHeader({ className, ...props }: React.ComponentProps<"div">): React.ReactElement {
    return (
        <div
            className={cn("flex flex-col gap-2 p-6 pb-3 max-sm:pb-4", className)}
            data-slot={"dialog-header"}
            {...props}
        />
    );
}

export function DialogFooter({
    className,
    variant = "default",
    ...props
}: React.ComponentProps<"div"> & {
    variant?: "default" | "bare";
}): React.ReactElement {
    return (
        <div
            className={cn(
                "flex flex-col-reverse gap-2 px-6 sm:flex-row sm:justify-end sm:rounded-b-[calc(var(--radius-2xl)-1px)]",
                variant === "default" && "border-t bg-muted/72 py-4",
                variant === "bare" && "pt-4 pb-6 [[data-slot=dialog-panel-scroll]~&]:pt-3",
                className,
            )}
            data-slot={"dialog-footer"}
            {...props}
        />
    );
}

export function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props): React.ReactElement {
    return (
        <DialogPrimitive.Title
            className={cn("font-heading font-semibold text-xl leading-none", className)}
            data-slot={"dialog-title"}
            {...props}
        />
    );
}

export function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props): React.ReactElement {
    return (
        <DialogPrimitive.Description
            className={cn("text-muted-foreground text-sm", className)}
            data-slot={"dialog-description"}
            {...props}
        />
    );
}

export function DialogPanel({
    className,
    scrollFade = true,
    ...props
}: React.ComponentProps<"div"> & { scrollFade?: boolean }): React.ReactElement {
    return (
        // Slot on the wrapper so the footer rule is a sibling selector; :has() on the popup made charts crawl.
        <ScrollArea data-slot={"dialog-panel-scroll"} scrollFade={scrollFade}>
            <div
                className={cn(
                    "p-6 pt-1 [[data-slot=dialog-panel-scroll]:has(~[data-slot=dialog-footer]:not(.border-t))_&]:pb-1",
                    className,
                )}
                data-slot={"dialog-panel"}
                {...props}
            />
        </ScrollArea>
    );
}

export { DialogPrimitive, DialogBackdrop as DialogOverlay, DialogPopup as DialogContent };
